/**
 * kitchen-idempotence.ts — Anti-double impression des groupes de KOT
 * (lot 4 chantier CHR, ferme la limite documentée du lot 4a impression).
 *
 * Problème : en split multi-fonction, si le groupe 2 échoue après que le
 * groupe 1 est sorti, le retry du serveur ré-imprimait le groupe 1
 * (plat lancé deux fois en cuisine).
 *
 * Solution : mémoire process des groupes imprimés avec succès, clé
 * `orderNumber × fonction`. PRÉCONDITION : les orderNumber doivent être
 * UNIQUES PAR ENVOI (suffixe horodaté — vérifié sur les 3 générateurs :
 * waiter-pad useWaiterMutations, chr/TableSession, caisse-restaurant
 * useRestaurantSession, alignés lot 4). Un NOUVEL envoi imprime toujours ;
 * seul le RETRY du même envoi saute les groupes déjà sortis. TTL court :
 * au-delà, une ré-impression volontaire redevient possible.
 *
 * Portée assumée : mémoire locale au device (le retry vient du même
 * poste — toast d'échec sur la tablette/caisse qui a envoyé). Une
 * idempotence cross-device exigerait un verrou DB, hors scope V1.
 */

const TTL_MS = 15 * 60 * 1000;

const printedAt = new Map<string, number>();

const keyOf = (orderNumber: string, printerFunction: string | null): string =>
  `${orderNumber}|${printerFunction ?? 'default'}`;

function evictExpired(now: number): void {
  for (const [key, ts] of printedAt) {
    if (now - ts > TTL_MS) printedAt.delete(key);
  }
}

/** Le groupe de ce bon est-il déjà sorti avec succès (dans la fenêtre TTL) ? */
export function wasGroupPrinted(orderNumber: string, printerFunction: string | null): boolean {
  const now = Date.now();
  evictExpired(now);
  return printedAt.has(keyOf(orderNumber, printerFunction));
}

/** Marque le groupe comme imprimé avec succès. */
export function markGroupPrinted(orderNumber: string, printerFunction: string | null): void {
  printedAt.set(keyOf(orderNumber, printerFunction), Date.now());
}

/** Reset complet — usage tests uniquement. */
export function resetKitchenIdempotence(): void {
  printedAt.clear();
}
